import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CssBaseline, Box, Typography, IconButton, Table, TableBody, TableCell,
  tableCellClasses, TableContainer, TableHead, TableRow, Paper, CircularProgress, Chip,
  TablePagination,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Header from '../Includes/Header';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import { getPaymentBreakdown } from '../Apihelper/Dashboard.js';

const BG = '#F4F7F4';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 'bold',
    borderBottom: 'none',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
  '&:last-child td, &:last-child th': { border: 0 },
}));

// Drill-down page behind the dashboard's "Quick Stats" cards — shows the
// individual payment records that make up a stat's total. Only reachable by
// clicking a card (state passed via navigate); not listed in the sidebar.
const PaymentBreakdown = () => {
  const { colors: appColors } = useAppTheme();
  const navigate = useNavigate();
  const { state } = useLocation();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (!state?.source) {
      navigate('/dashboard');
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const userData = JSON.parse(localStorage.getItem('userdata'));
        const res = await getPaymentBreakdown(userData.token, {
          source: state.source,
          scope: state.scope,
          filter_type: state.filterType,
          year: state.year,
          month: state.month,
          limit: rowsPerPage,
          index: page * rowsPerPage,
        });
        if (res.code === 200) {
          setRows(Array.isArray(res.data) ? res.data : []);
          setTotalAmount(res.total_amount ?? 0);
          setTotalCount(res.total_count ?? 0);
        }
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [state, navigate, page, rowsPerPage]);

  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  if (!state?.source) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, minHeight: '100vh', backgroundColor: BG }}>
      <CssBaseline />
      <Header />

      <Box component="main" sx={{ flexGrow: 1, overflowX: 'hidden', overflowY: 'auto', p: { xs: '12px', sm: '20px', md: '24px' }, mt: { xs: '56px', sm: '64px' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <IconButton onClick={() => navigate('/dashboard')} sx={{ bgcolor: '#fff', boxShadow: '0 2px 8px rgba(27,67,50,0.1)' }}>
            <ArrowBackIcon sx={{ color: appColors.leaf }} />
          </IconButton>
          <Box>
            <Typography sx={{ fontSize: { xs: '20px', sm: '24px' }, fontWeight: 800, color: appColors.leaf }}>
              {state.label || 'Payment Breakdown'}
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: '#64748b', mt: 0.25 }}>
              {state.periodLabel || ''}
            </Typography>
          </Box>
        </Box>

        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#fff', borderRadius: '14px', p: 2, mb: 2.5,
          border: '1px solid rgba(27,67,50,0.08)', boxShadow: '0 2px 12px rgba(27,67,50,0.07)',
        }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>
            {totalCount} record{totalCount === 1 ? '' : 's'}
          </Typography>
          <Chip
            label={`Total: ₹${Number(totalAmount).toLocaleString('en-IN')}`}
            sx={{ bgcolor: `${appColors.leaf}15`, color: appColors.leaf, fontWeight: 800 }}
          />
        </Box>

        <TableContainer component={Paper} sx={{ borderRadius: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
          {loading ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <CircularProgress sx={{ color: appColors.leaf }} />
            </Box>
          ) : (
            <Table sx={{ minWidth: 600 }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell>S.NO</StyledTableCell>
                  <StyledTableCell>NAME</StyledTableCell>
                  <StyledTableCell>TYPE</StyledTableCell>
                  <StyledTableCell>MODE</StyledTableCell>
                  <StyledTableCell>DATE</StyledTableCell>
                  <StyledTableCell align="right">AMOUNT</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: '#94a3b8' }}>
                      No records found for this period.
                    </TableCell>
                  </TableRow>
                ) : rows.map((row, i) => (
                  <StyledTableRow key={i}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>{row.mode || '—'}</TableCell>
                    <TableCell>{row.date}</TableCell>
                    <TableCell align="right">₹{Number(row.amount).toLocaleString('en-IN')}</TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 20, 50]}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton
          showLastButton
          sx={{ '& .MuiTablePagination-actions': { mr: 8 } }}
          labelDisplayedRows={({ page: p, count }) =>
            `Page ${p + 1} of ${Math.ceil(count / rowsPerPage)}  |  Total: ${count} records`
          }
        />
      </Box>
    </Box>
  );
};

export default PaymentBreakdown;
